import { createApp } from 'vue'
import App from './App.vue'
import { routes } from './routes.js'
import { createRouter, createWebHistory } from 'vue-router'
import { createPinia } from 'pinia'
import qs from 'qs'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'
import { useAnalytics } from './composables/useAnalytics'
import { useAttribution } from './composables/useAttribution'
import { loadPylon } from './composables/pylon/loadPylon'

import '@serif-health/ui-library/style.css'
import './tailwind.css'

async function enableMocking() {
    if (!import.meta.env.DEV) {
        return
    }

    if (import.meta.env.VITE_MOCKING_ENABLED !== 'true') {
        return
    }

    const { worker } = await import('./mocks/browser')

    // `worker.start()` returns a Promise that resolves
    // once the Service Worker is up and ready to intercept requests.
    return worker.start()
}

async function startApp() {
    await enableMocking()

    const queryClient = new QueryClient()

    /**
     * Init Meticulous
     * Must run before any other scripts (e.g. PostHog) to ensure
     * all network requests are captured from the start.
     */
    // Record all sessions on localhost, staging stacks and preview URLs
    if (!isProduction()) {
        // Start the Meticulous recorder before you initialise your app.
        // Note: all errors are caught and logged, so no need to surround with try/catch
        const { tryLoadAndStartRecorder } = await import('@alwaysmeticulous/recorder-loader')
        await tryLoadAndStartRecorder({
            recordingToken: 'LCEYKJaQobzrLTzvb84thJy1BvGa3Je1ZoJp31GX',
            isProduction: false,
        })

        console.info('Meticulous Recording initialized')
    }

    loadPylon()

    const { analytics } = useAnalytics()
    useAttribution().remember(window.location.search)

    const app = createApp(App)

    const router = createRouter({
        history: createWebHistory(),
        routes,
        parseQuery: (q) => qs.parse(q, { arrayLimit: Infinity, parseArrays: true }),
        stringifyQuery: (q) => qs.stringify(q, { encodeValuesOnly: true, indices: true }),
    })

    // Vue catches component errors before they reach window, so PostHog's autocapture never sees them.
    if (import.meta.env.PROD) {
        app.config.errorHandler = (error) => {
            console.error(error)
            analytics.captureException(error)
        }
    }

    app.use(router).use(createPinia()).use(VueQueryPlugin, { queryClient, enableDevtoolsV6Plugin: false }).mount('#app')
}

function isProduction() {
    const hostname = window.location.hostname
    return hostname.indexOf('serifhealth.com') > -1 || hostname.endsWith('.vercel.app')
}

startApp()

console.info(`Loading Serif Health app, ${import.meta.env.VITE_APP_ENV} environment.`)
